import React, { useEffect, useState } from "react"
import { toast } from "react-toastify"
import moment from "moment"
import MyAlert from "@common-utils/MyAlert"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import {
     IArchiveChecklistDetailModel,
     IArchiveChecklistListItemModel,
     personnelArchiveChecklistGetRequest,
     personnelArchiveChecklistToggleRequest,
     personnelArchiveChecklistsRequest,
} from "../core/_requests"

const TAKEOUT_LINK = "https://admin.google.com/ac/customertakeout"
const DRIVE_LINK = "https://drive.google.com/drive/u/2/folders/0ABFP2RJKAaEpUk9PVA"
const GROUPS_LINK = "https://admin.google.com/ac/groups"
const USERS_LINK = "https://admin.google.com/ac/users"

const TASK_DEFS: { key: string; label: string; link?: string; linkLabel?: string }[] = [
     { key: "contactResponsable", label: "Contacter le responsable — vérifier s'il a besoin d'un accès prolongé à la boîte mail du collaborateur avant l'archivage." },
     { key: "export", label: "Exporter les données du compte Google.", link: TAKEOUT_LINK, linkLabel: "Ouvrir Data export" },
     { key: "archiveToDrive", label: "Télécharger l'archive depuis le bucket et la déposer sur le Drive.", link: DRIVE_LINK, linkLabel: "Ouvrir le dossier Drive" },
     { key: "deleteGroups", label: "Supprimer les groupes GWS.", link: GROUPS_LINK, linkLabel: "Ouvrir Groupes" },
     { key: "deleteAccount", label: "Supprimer le compte Google.", link: USERS_LINK, linkLabel: "Ouvrir Utilisateurs" },
]

const ArchiveChecklistPage = () => {
     const [list, setList] = useState<IArchiveChecklistListItemModel[] | null>(null)
     const [details, setDetails] = useState<Record<number, IArchiveChecklistDetailModel>>({})
     const [expandedId, setExpandedId] = useState<number | null>(null)

     useEffect(() => {
          personnelArchiveChecklistsRequest()
               .then(r => setList(r.data))
               .catch(() => toast.error("Impossible de charger les tâches post-archive."))
     }, [])

     function toggleExpand(id: number) {
          if (expandedId === id) {
               setExpandedId(null)
               return
          }
          setExpandedId(id)
          if (!details[id]) {
               personnelArchiveChecklistGetRequest(id)
                    .then(r => setDetails(prev => ({ ...prev, [id]: r.data })))
                    .catch(() => toast.error("Impossible de charger le détail."))
          }
     }

     function onToggleTask(id: number, task: string, done: boolean) {
          personnelArchiveChecklistToggleRequest(id, task, done)
               .then(r => {
                    setDetails(prev => ({ ...prev, [id]: { ...prev[id], tasks: r.data.tasks, completedAt: r.data.completedAt } }))
                    if (r.data.completedAt) {
                         setList(prev => (prev ? prev.filter(c => c.id !== id) : null))
                         toast.success("Checklist terminée ✓")
                    } else {
                         const nbDone = Object.values(r.data.tasks).filter(Boolean).length
                         setList(prev => (prev ? prev.map(c => (c.id === id ? { ...c, nbDone } : c)) : null))
                    }
               })
               .catch(() => toast.error("Erreur lors de la mise à jour."))
     }

     return (
          <div className="container">
               <p className="text-muted mb-5">
                    Tâches d'administration Google à finaliser après la sortie des effectifs d'un collaborateur. adminsi@ est relancé tous les 3 jours
                    tant qu'une checklist n'est pas complète.
               </p>

               {!list && (
                    <div className="text-center p-4">
                         <MySimpleSpinner />
                    </div>
               )}

               {list && list.length === 0 && <MyAlert type={"success"}>Aucune tâche post-archive en attente. 🎉</MyAlert>}

               {list &&
                    list.map(item => {
                         const detail = details[item.id]
                         const isOpen = expandedId === item.id
                         return (
                              <div key={item.id} className="card mb-4">
                                   <div className="card-body">
                                        <div className="d-flex justify-content-between align-items-center cursor-pointer" onClick={() => toggleExpand(item.id)}>
                                             <div>
                                                  <span className="fw-bold fs-5">{item.personnelNom}</span>
                                                  <span className="text-muted ms-2">{item.gwsEmail}</span>
                                                  <div className="text-muted fs-8">Archivé le {moment(item.createdAt).format("dddd D MMMM YYYY")}</div>
                                             </div>
                                             <div className="d-flex align-items-center">
                                                  <span className="badge badge-light-warning me-3">
                                                       {item.nbDone}/{item.nbTotal} fait
                                                  </span>
                                                  <i className={`fas ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`} />
                                             </div>
                                        </div>

                                        {isOpen && !detail && (
                                             <div className="text-center p-3">
                                                  <MySimpleSpinner size={"sm"} />
                                             </div>
                                        )}

                                        {isOpen && detail && (
                                             <div className="mt-4">
                                                  {TASK_DEFS.map(def => {
                                                       const done = !!detail.tasks[def.key]
                                                       return (
                                                            <div key={def.key} className="d-flex align-items-start py-3 border-top">
                                                                 <div className="form-check form-check-custom form-check-solid me-3 mt-1">
                                                                      <input
                                                                           type="checkbox"
                                                                           className="form-check-input cursor-pointer"
                                                                           checked={done}
                                                                           onChange={e => onToggleTask(item.id, def.key, e.target.checked)}
                                                                      />
                                                                 </div>
                                                                 <div className="flex-grow-1">
                                                                      <div className={done ? "text-muted text-decoration-line-through" : ""}>{def.label}</div>

                                                                      {def.key === "deleteGroups" && detail.gwsGroupEmails.length > 0 && (
                                                                           <div className="mt-1">
                                                                                {detail.gwsGroupEmails.map(g => (
                                                                                     <span key={g} className="badge badge-light-danger me-1">
                                                                                          {g}
                                                                                     </span>
                                                                                ))}
                                                                           </div>
                                                                      )}
                                                                      {def.key === "deleteGroups" && detail.gwsGroupEmails.length === 0 && (
                                                                           <div className="text-muted fs-8 mt-1">Aucun groupe GWS enregistré.</div>
                                                                      )}
                                                                      {def.key === "deleteAccount" && detail.gwsEmail && (
                                                                           <div className="mt-1">
                                                                                <span className="badge badge-light-danger">{detail.gwsEmail}</span>
                                                                           </div>
                                                                      )}

                                                                      {def.link && (
                                                                           <a href={def.link} target="_blank" rel="noreferrer" className="d-inline-block mt-1 fs-8">
                                                                                {def.linkLabel} <i className="fas fa-external-link-alt fs-9" />
                                                                           </a>
                                                                      )}
                                                                 </div>
                                                            </div>
                                                       )
                                                  })}
                                             </div>
                                        )}
                                   </div>
                              </div>
                         )
                    })}
          </div>
     )
}

export default ArchiveChecklistPage
